import { ApolloQueryResult, useQuery } from '@apollo/client';
import toNumber from 'lodash/toNumber';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { Radio } from '@atlaskit/radio';
import Textfield from '@atlaskit/textfield';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  EntitlementGroup,
  FetchEntitlementsByBillingDateQuery,
  FetchEntitlementsByBillingDateQueryVariables,
  LinkedEntitlementTypes,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { NEW_DATE_FORMAT, UpdateOrderRequest, CustomOrderEntitlement } from '@atlassian/cssxp-typescommon';
import { getUserTier, constructCustomOrderRequest } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  sentenceCase,
  ENABLE_OMS_SERVICE_V2,
} from '@atlassian/cssxp-utils-generic';
import { addMonths, addYears, format, isAfter, isBefore, parse, startOfDay } from 'date-fns';
import defaultTo from 'lodash/defaultTo';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { ErrorComponents, InfoPanel } from '../..';
import { HighlightedDatePicker } from '../../date-picker';
import EntitlementIcon from '../../entitlement-icon';
import {
  getParentContainerId,
  LinkedEntitlementsTable,
  getLinkedEntitlementsDetails,
  getChargeElement,
} from '../../entitlement-utils';
import { FormattedDate } from '../../formatted';
import { useFormSubmit, useNotification, useSidebar } from '../../hooks';
import { useFeatureToggle } from '../../hooks/src/use-feature-toggle';
import { NotificationTypes } from '../../notifications';
import { successNotificationBuilder } from '../../notifications/utils/notification-builder';
import { CoBillWarningDialog } from './co-bill/warning-dialog';
import { CoBillingCommon } from './co-bill/co-billing-component';
import { fetchEntitlementsByBillingDate } from './queries/fetch-entitlements-by-billing-date';

import './set-co-bill-date.scss';

const DATE_FORMAT_FOR_DATE_PICKER = 'MMM DD, YYYY';
const DATE_FORMAT_TO_PARSE = 'yyyy-MM-dd';
const DATE_FORMAT_TO_FORMAT = 'MMM d, yyyy';

interface Props {
  invoiceGroupId: string;
  product: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isMetaDataHidden?: boolean;
  billingFrequency?: string;
}

interface FormData {
  newSelectedBillDate?: string | null;
  supportTicketNumber?: string | null;
  handNote?: string | null;
}

const formatSelectedDate = (selectedDate: string): string => {
  const formattedSelectedDate = format(parse(selectedDate, DATE_FORMAT_TO_PARSE, new Date()), DATE_FORMAT_TO_FORMAT);
  return formattedSelectedDate;
};

const constructMetadata = (formData: FormData) => {
  const metadata: Record<string, string> = {};
  if (formData.handNote) {
    metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
  }
  if (formData.supportTicketNumber) {
    metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
  }
  return metadata;
};

const SetCoBillDate: React.FC<Props> = ({
  invoiceGroupId,
  product,
  refetch,
  isMetaDataHidden = false,
  billingFrequency,
}) => {
  const { productId, id, latestPeriodEndDate, billingFrequency: entitlementBillingFrequency } = product;
  const [selectedBillingDate, setSelectedBillingDate] = React.useState('');
  const [isConfirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [radioValue, setRadioValue] = React.useState<string>('new-bill-date');
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = useState(false);

  const [isEnabled] = useFeatureToggle();
  const isEntRelationShipFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);

  const isRelationshipExists = useMemo(() => {
    if (!product?.relatesToEntitlements && !product?.relatesFromEntitlements) {
      return false;
    }
    return isEntRelationShipFeatureEnable;
  }, [product, isEntRelationShipFeatureEnable]);

  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
  });

  const [linkedEntitlementsData, setLinkedEntitlementsData] = useState<Array<LinkedDestinationEntitlement>>([]);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const HEADER_INFO = `To synchronize payments, align the bill date of this subscription with other subscriptions in this billing profile. Pro-rata adjustments will be applied to the next invoice.`;

  const getNotificationOption = (id: string) => {
    const notificationOptions = successNotificationBuilder();
    return notificationOptions
      ?.withId(id)
      .withHeader('Bill date changed')
      .withMessage(
        <div>
          <div>This change will be in effect from the next billing cycle.</div>
        </div>
      )
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };

  if (complete && !notificationSent) {
    const notificationOptions = getNotificationOption('set-bill-date-success');
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  const constructRequestData = useCallback(
    (formData: FormData) => {
      const requestData: UpdateOrderRequest = {
        entitlementId: id,
        productId: productId || '',
        nextBillingAnchorTimestamp: selectedBillingDate
          ? moment.utc(selectedBillingDate).valueOf().toString()
          : undefined,
        metadata: constructMetadata(formData),
        units: isStatuspage(product?.productId)
          ? (product?.unitCount as string)
          : getUserTier(product?.subscription)?.toString(),
        chargeElement: getChargeElement(product?.productId as string),
      };

      if (isRelationshipExists) {
        requestData.linkedEntitlements = linkedEntitlementsData.length
          ? getLinkedEntitlementsDetails(
              product,
              linkedEntitlementsData.filter(
                (linkedEntitlementObj) => linkedEntitlementObj.billingStatus === BillingStatus.ACTIVE
              )
            )
          : [];
      }

      if (isOmsServiceV2Enabled) {
        const nextBillingAnchorTimestamp = selectedBillingDate ? moment.utc(selectedBillingDate).valueOf() : undefined;
        const entitlementsList: CustomOrderEntitlement[] = [
          {
            entitlementId: id,
            productId: productId as string,
            offeringId: product?.offeringId as string,
            nextBillingAnchorTimestamp: nextBillingAnchorTimestamp,
          },
        ];

        if (isRelationshipExists) {
          linkedEntitlementsData?.forEach((linkedEntitlement) => {
            entitlementsList.push({
              entitlementId: linkedEntitlement.entitlementId,
              productId: linkedEntitlement.productId as string,
              offeringId: linkedEntitlement.offeringId as string,
              nextBillingAnchorTimestamp: nextBillingAnchorTimestamp,
            });
          });
        }

        const constructV2Request = {
          metadata: constructMetadata(formData),
          orderType: OrderItemType.AMENDMENT_ORDER,
          entitlements: entitlementsList,
          transactionAccountId: product?.transactionAccountId as string,
          invoiceGroupId,
        };
        return constructCustomOrderRequest(constructV2Request);
      }
      return requestData;
    },
    [id, productId, selectedBillingDate, isRelationshipExists, linkedEntitlementsData, product]
  );

  const handleFormSubmit = useCallback(
    (formData: FormData) => {
      logTimeStamp(new Date().getTime(), 'Set Bill Date', SignalFxFEPerfStore.FE_USER_TASK_SET_BILL_DATE_TIME);
      if (selectedBillingDate !== moment.utc(latestPeriodEndDate).format(NEW_DATE_FORMAT)) {
        setConfirmDialogOpen(true);
      } else {
        const requestData = constructRequestData(formData);
        submitForm(requestData);
      }
    },
    [constructRequestData, latestPeriodEndDate, selectedBillingDate, submitForm]
  );

  const onConfirmDialog = (formData: FormData) => {
    setConfirmDialogOpen(false);
    const requestData = constructRequestData(formData);
    submitForm(requestData);
  };

  const { data, loading, error } = useQuery<
    FetchEntitlementsByBillingDateQuery,
    FetchEntitlementsByBillingDateQueryVariables
  >(fetchEntitlementsByBillingDate, {
    variables: {
      id: invoiceGroupId,
      entitlementId: id,
      filterOptions: billingFrequency ? [{ key: 'billingFrequency', value: sentenceCase(billingFrequency) }] : [],
    },
  });

  const upcomingFinalBillDate = useMemo(() => {
    if (latestPeriodEndDate) {
      const latestPeriodDate = startOfDay(new Date(latestPeriodEndDate));
      let upcomingBillDate = addMonths(latestPeriodDate, 1);
      if (entitlementBillingFrequency === BillingFrequency.ANNUAL) {
        upcomingBillDate = addYears(latestPeriodDate, 1);
      }
      return upcomingBillDate;
    }
  }, [entitlementBillingFrequency, latestPeriodEndDate]);

  const handleDisableDateFilter = (date: string) => {
    if (latestPeriodEndDate) {
      const latestPeriodDate = startOfDay(new Date(latestPeriodEndDate));
      const currentStartOfDay = startOfDay(new Date(date));
      if (isBefore(currentStartOfDay, latestPeriodDate)) {
        return true;
      }
      if (upcomingFinalBillDate && isAfter(currentStartOfDay, upcomingFinalBillDate)) {
        return true;
      }
    }
    return false;
  };

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  const entitlementsGroupByBillingDate = data?.entitlementsGroupByBillingDate;
  const billingDateGroup = entitlementsGroupByBillingDate?.billingDateGroup ?? [];

  let nextBillDate = format(new Date(), DATE_FORMAT_TO_FORMAT);
  if (latestPeriodEndDate) {
    nextBillDate = format(new Date(latestPeriodEndDate), DATE_FORMAT_TO_FORMAT);
  }

  const handleRadioChange = ({ currentTarget: { value } }: React.SyntheticEvent<any>, formData?: FormData) => {
    if (value !== 'new-bill-date') {
      setSelectedBillingDate(value);
    } else {
      setSelectedBillingDate(formData?.newSelectedBillDate ?? nextBillDate);
    }
    setRadioValue(value);
  };

  return (
    <div className="set-bill-date-container" data-testid="set-bill-date-container">
      <div className="set-bill-date-container__info">
        <div className="header">
          <FormHeader title="Change bill date">
            <EntitlementIcon
              logoId={product?.logoId as string}
              productId={product?.productId as string}
              isCloud={true}
              className="avatar medium"
            />{' '}
            <h4 style={{ display: 'inline' }}>{`${defaultTo(product?.productName, 'Unnamed product')} ${
              product?.entitlementNumber
            }`}</h4>
            &nbsp;&nbsp;
          </FormHeader>
        </div>
        {product?.cloudSiteHostname && (
          <span>
            Site name: <span>{product?.cloudSiteHostname}</span>
          </span>
        )}
        {!isRelationshipExists && <h2 className="set-bill-date-container__info__subheading">{HEADER_INFO}</h2>}
        {isRelationshipExists && (
          <LinkedEntitlementsTable
            showBillDateColumn={true}
            message={'Performing this action will automatically change the bill date of these subscriptions:'}
            entitlementId={product?.id ?? ''}
            parentContainerID={getParentContainerId(product)}
            productType={product.productType}
            currentEntitlementType={LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT}
            destinationEntitlementType={[
              LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
              LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
              LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
              LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
            ]}
            findApps={true}
            findProducts={true}
            updateLinkedEntitlements={setLinkedEntitlementsData}
            setLoading={setLoadingLinkedEntitlements}
          />
        )}
        <div className="set-bill-date-container__info__subscription">
          <div className="set-bill-date-container__info__subscription__col">
            <div className="set-bill-date-container__info__subscription__col__header-cell">Current bill date</div>
            {latestPeriodEndDate && (
              <span className="set-bill-date-container__info__subscription__col__cell mt-2">
                <FormattedDate date={latestPeriodEndDate} format={NEW_DATE_FORMAT} />
              </span>
            )}
          </div>
        </div>
        <Form onSubmit={handleFormSubmit}>
          {({ formProps, submitting, getValues }) => (
            <form {...formProps} className="set-bill-date-container__info__form">
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to set billing date for the following reason"
                      message={formError || 'Something went wrong.'}
                    />
                  }
                />
              )}
              <h2 className="set-bill-date-container__info__choose-window">
                Select a new bill date between {nextBillDate}{' '}
                {upcomingFinalBillDate ? ` - ${format(new Date(upcomingFinalBillDate), DATE_FORMAT_TO_FORMAT)}` : ''}
              </h2>
              <div className="new-bill-date-container">
                <div className="radio-button-container">
                  <Radio
                    isChecked={'new-bill-date' === radioValue}
                    onChange={(event) => handleRadioChange(event, getValues())}
                    name={`key_new-bill-date`}
                    value={'new-bill-date'}
                    testId="new-bill-date-radio"
                  />
                </div>
                <div className="new-bill-date-comp">
                  <FormSection>
                    <Field name="newSelectedBillDate" label="New bill date" defaultValue={nextBillDate}>
                      {({ fieldProps: { id, ...rest }, error }) => (
                        <Fragment>
                          <HighlightedDatePicker
                            selectProps={{ inputId: id }}
                            {...rest}
                            dateFormat={DATE_FORMAT_FOR_DATE_PICKER}
                            defaultValue={nextBillDate}
                            disabledDateFilter={handleDisableDateFilter}
                            onChange={(selectedDate) => {
                              setSelectedBillingDate(formatSelectedDate(selectedDate));
                              rest.onChange(selectedDate);
                            }}
                          />
                          {error && <ErrorMessage>{error}</ErrorMessage>}
                        </Fragment>
                      )}
                    </Field>
                  </FormSection>
                </div>
              </div>
              <div className="existing-subscriptions-container">
                <h4 id="co-bill-sticky-subheader" className="existing-subscriptions-container__sticky-subheading">
                  Or select a date that matches other subscriptions
                </h4>
                <CoBillingCommon
                  billingDateGroup={billingDateGroup as EntitlementGroup[]}
                  product={product}
                  radioValue={radioValue}
                  selectedBillingDate={selectedBillingDate}
                  handleBillDateChange={handleRadioChange}
                />
              </div>
              {!isMetaDataHidden && (
                <>
                  <FormSection>
                    <Field
                      name="supportTicketNumber"
                      label={'Support ticket link'}
                      isRequired={true}
                      validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                    >
                      {({ fieldProps, error }) => (
                        <>
                          <Textfield
                            {...fieldProps}
                            name="supportTicketNumber"
                            placeholder={'Add GSAC ticket number'}
                            testId="support-ticket-number"
                          />
                          {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                        </>
                      )}
                    </Field>
                  </FormSection>
                  <FormSection>
                    <Field
                      label={'Internal comment'}
                      name="handNote"
                      isRequired
                      validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                    >
                      {({ fieldProps, error }) => (
                        <>
                          <Textfield
                            {...fieldProps}
                            name="handNote"
                            testId="hand-note"
                            placeholder={'Add order comments'}
                          />
                          {error && (
                            <ErrorMessage testId="supportTicketLink-error">This field is required</ErrorMessage>
                          )}
                        </>
                      )}
                    </Field>
                  </FormSection>
                </>
              )}

              <div className="button-info-text">Pro-rata adjustments will be applied to the next invoice</div>
              <FormFooter>
                <ButtonGroup>
                  <Button onClick={() => closeSidebar()} testId="bill-cancel-button">
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    appearance="primary"
                    isDisabled={!selectedBillingDate || loadingLinkedEntitlement}
                    testId="bill-change-button"
                    isLoading={submitting || isProcessing}
                    aria-label="Change bill date"
                    className="change-bill-date-button"
                  >
                    Change bill date
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
              <CoBillWarningDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => onConfirmDialog(getValues())}
                entitlement={product}
                selectedDate={selectedBillingDate}
                existingEntitlements={billingDateGroup as EntitlementGroup[]}
                isRelationshipExists={isRelationshipExists}
              />
            </form>
          )}
        </Form>
      </div>
    </div>
  );
};

export default SetCoBillDate;
